/**
 * useSendVnMessage — TanStack mutation for the broker→VN free-text message
 * (Wave W5 broker→VN messaging; docs/design/2026-07-29_mandant-config-catalog.md → W5).
 *
 * POST /communications/vn-messages (BrokerMessageController): the message is
 * delivered via the VN's RESOLVED channel — a portal-VN gets a portal-thread
 * row + a timely notification; an email-only VN gets a broker-branded email.
 * The created OUTBOUND communication then surfaces in the broker's Kommunikation
 * Ausgang/Gesendet tabs, so on success we invalidate BOTH live outbound buckets
 * (the mutation renders the server's outbound row — no client-side domain logic).
 *
 * [Source: apps/web/src/features/communication-v2/hooks/useEingangMails.ts] (mutation + invalidate pattern)
 * [Source: apps/api/src/modules/customer-portal/broker-message.controller.ts] (the endpoint)
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../lib/api-client';
import type { SendVnMessageBody, SendVnMessageResult } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';

/** Send a broker→VN message; on success refetch the live outbound buckets so the row appears. */
export function useSendVnMessage() {
  const qc = useQueryClient();
  return useMutation<SendVnMessageResult, Error, SendVnMessageBody>({
    mutationFn: (body) => apiClient.communications.sendVnMessage(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.communications('gesendet', { mode: 'live' }) });
      void qc.invalidateQueries({ queryKey: qk.communications('ausgang_entwuerfe', { mode: 'live' }) });
    },
  });
}
